import { useState } from "react";
import { Link } from "wouter";
import { motion } from "framer-motion";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Mail, Facebook, Twitter, Instagram, Linkedin, Send, MapPin, Phone, Mailbox } from "lucide-react";
import toast from 'react-hot-toast';

export default function Footer() {
  const [email, setEmail] = useState("");
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    message: ''
  });

  const handleNewsletter = (e: React.FormEvent) => {
    e.preventDefault();
    console.log("Subscribed with:", email);
    setEmail("");
  };

  const handleContactSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(contactForm)
      });

      if (response.ok) {
        toast.success('Message sent successfully!');
        setContactForm({ name: '', email: '', message: '' });
      } else {
        throw new Error('Failed to send message');
      }
    } catch (error) {
      toast.error('Error sending message. Please try again.');
    }
  };

  const services = [
    { name: "Software Development", href: "/software-development" },
    { name: "Web Development", href: "/web-design-development" },
    { name: "Mobile Apps", href: "/mobile-app-development" },
    { name: "Digital Marketing", href: "/digital-marketing" },
    { name: "eCommerce", href: "/ecommerce" },
    { name: "Cloud Services", href: "/cloud-services" },
    { name: "Maintenance & Support", href: "/maintenance-support" },
    { name: "All Services", href: "/services" },
  ];

  const company = [
    { name: "About Us", href: "/about" },
    { name: "Portfolio", href: "/portfolio" },
    { name: "Careers", href: "/careers" },
    { name: "Blog", href: "/blog" },
    { name: "Contact", href: "/contact" },
    { name: "Privacy Policy", href: "/privacy-policy" },
    { name: "Terms of Service", href: "/terms-of-service" },
    { name: "Cookie Policy", href: "/cookie-policy" },
  ];

  const socialLinks = [
    { name: "Facebook", href: "#", icon: Facebook },
    { name: "Twitter", href: "#", icon: Twitter },
    { name: "Instagram", href: "#", icon: Instagram },
    { name: "LinkedIn", href: "#", icon: Linkedin },
  ];

  const contactInfo = [
    { icon: MapPin, text: "Sharjah, UAE" },
    { icon: Phone, text: "+971-58-248-1443" },
    { icon: Mailbox, text: "info@firstsoft.ae" },
  ];

  return (
    <footer className="bg-white py-16 text-gray-900">
      <div className="container mx-auto px-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Company Info */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="lg:col-span-1"
          >
            <div className="flex items-center space-x-3 mb-6 group">
              <img 
                src="/images/firstsoftlogo.webp" 
                alt="First Soft UAE Logo" 
                className="w-12 h-12 rounded-lg shadow-[0_4px_8px_rgba(124,58,237,0.3),0_8px_16px_rgba(124,58,237,0.2)] transition-all duration-300 group-hover:shadow-[0_6px_12px_rgba(124,58,237,0.4),0_12px_24px_rgba(124,58,237,0.3)]" 
              />
              <div className="flex flex-col justify-center items-center">
                <span className="font-bold text-xl leading-tight tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-purple-700 via-pink-600 to-purple-700 bg-[length:200%] px-1.5 py-0.5 rounded-md bg-gray-900/80 backdrop-blur-sm border border-purple-300/50 shadow-lg">
                  First <span className="text-black font-bold">Soft UAE</span>
                </span>
                <span className="text-[0.65rem] font-medium uppercase tracking-[0.15em] text-purple-600/70 mt-0.5 bg-purple-100/50 px-1.5 py-0.5 rounded-full">
                Technologies
                </span>
              </div>
            </div>
            <p className="text-gray-700 mb-4 font-medium">We Architect Tomorrow's Tech</p>
            <p className="text-sm text-gray-600 mb-6">
              Leading software development company in Sharjah, UAE, delivering cutting-edge digital solutions.
            </p>
            
            {/* Contact Info */}
            <div className="space-y-3 mb-6">
              {contactInfo.map((item, index) => {
                const Icon = item.icon;
                return (
                  <div key={index} className="flex items-center space-x-3">
                    <Icon className="h-4 w-4 text-gray-600" />
                    <span className="text-sm text-gray-600">{item.text}</span>
                  </div>
                );
              })}
            </div>
            
            {/* Social Media Links */}
            <div className="flex space-x-4">
              {socialLinks.map((social) => {
                const Icon = social.icon;
                return (
                  <a
                    key={social.name}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-600 hover:text-gray-900 transition-colors"
                    aria-label={`Visit our ${social.name} page`}
                    role="link"
                  >
                    <Icon className="h-5 w-5" />
                  </a>
                );
              })}
            </div>
          </motion.div>

          {/* Services */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <h3 className="font-semibold text-lg mb-6 text-gray-900">Services</h3>
            <ul className="space-y-3">
              {services.map((service) => (
                <li key={service.name}>
                  <Link
                    href={service.href}
                    className="text-gray-600 hover:text-gray-900 transition-colors text-sm"
                  >
                    {service.name}
                  </Link>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Cities Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="lg:col-span-1"
          >
            <h3 className="text-lg font-semibold mb-6">Web Design Services In</h3>
            <ul className="space-y-3">
              {[
                { name: "Dubai", href: "/web-design-dubai" },
                { name: "Al Ain", href: "/web-design-al-ain" },
                { name: "Sharjah", href: "/web-design-sharjah" },
                { name: "Ajman", href: "/web-design-ajman" },
                { name: "Fujairah", href: "/web-design-fujairah" },
                { name: "Ras Al Khaimah", href: "/web-design-ras-al-khaimah" },
                { name: "Umm Al Quwain", href: "/web-design-umm-al-quwain" },
                { name: "UAE", href: "/web-design-uae" },
                { name: "USA", href: "/web-design-usa" }
              ].map((city) => (
                <li key={city.name}>
                  <Link 
                    href={city.href}
                    className="text-sm text-gray-600 hover:text-gray-900 transition-colors"
                  >
                    {city.name}
                  </Link>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Company */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <h3 className="font-semibold text-lg mb-6 text-gray-900">Company</h3>
            <ul className="space-y-3">
              {company.map((item) => (
                <li key={item.name}>
                  <Link
                    href={item.href}
                    className="text-gray-600 hover:text-gray-900 transition-colors text-sm"
                  >
                    {item.name}
                  </Link>
                </li>
              ))}
            </ul>
          </motion.div>
        </div>

        {/* Newsletter */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="mt-16 bg-gray-100 rounded-lg p-6 border border-gray-300"
        >
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div className="space-y-2">
              <h3 className="font-semibold text-lg text-gray-900">Stay Updated</h3>
              <p className="text-gray-600 text-sm">
                Subscribe to our newsletter for the <span className="font-medium text-purple-600">latest updates</span> and <span className="font-medium text-pink-500">exclusive offers</span>.
              </p>
            </div>
            <form onSubmit={handleNewsletter} className="flex gap-2">
              <Input
                type="email"
                placeholder="Your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="bg-white border-gray-300 w-full md:w-64"
              />
              <Button type="submit" className="bg-gradient-to-r from-purple-600 to-pink-500 hover:from-purple-700 hover:to-pink-600 text-white shadow-md hover:shadow-lg transition-all whitespace-nowrap">
                Subscribe
              </Button>
            </form>
          </div>
        </motion.div>

        {/* Copyright */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.5 }}
          className="border-t border-gray-300 mt-12 pt-8 text-center"
        >
          <p className="text-gray-600">
            &copy; 2025 First Soft UAE. All rights reserved. | Sharjah, UAE
          </p>
        </motion.div>
      </div>
    </footer>
  );
}
